import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getAdminWrap,
    getAdminsWithAccess,
    grantAdmin,
    revokeAdmin,
    recovery,
    hasRecoveryKit,
    logRecoveryKitGenerated,
} from "~/services/gateways/orgEncryption.gateway";
import type { GrantAdminPayload, RecoveryPayload } from "~/domain/OrgEncryption";

export function useAdminWrap(enabled = true) {
    return useQuery({
        queryKey: ["org-encryption", "admin-wrap"],
        queryFn: getAdminWrap,
        refetchOnWindowFocus: false,
        retry: false,
        enabled,
    });
}

export function useAdminsWithAccess(enabled = true) {
    return useQuery({
        queryKey: ["org-encryption", "admins-with-access"],
        queryFn: getAdminsWithAccess,
        refetchOnWindowFocus: false,
        retry: false,
        enabled,
    });
}

export function useGrantAdmin() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: GrantAdminPayload) => grantAdmin(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["org-encryption"] });
        },
    });
}

export function useRevokeAdmin() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (userId: string) => revokeAdmin(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["org-encryption"] });
        },
    });
}

export function useRecovery() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: RecoveryPayload) => recovery(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["org-encryption"] });
        },
    });
}

export function useHasRecoveryKit(enabled = true) {
    return useQuery({
        queryKey: ["org-encryption", "has-recovery-kit"],
        queryFn: hasRecoveryKit,
        refetchOnWindowFocus: false,
        retry: false,
        enabled,
    });
}

export function useLogRecoveryKitGenerated() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => logRecoveryKitGenerated(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["org-encryption", "has-recovery-kit"] });
        },
    });
}
